import React, { useEffect, useMemo, useState } from "react";
import Select from 'react-select';
import moment from 'moment';

import PanelTemplate from "../../templates/Panel";
import Table from "../../components/Table";
import Box from "../../components/Box";
import Modal from "../../components/Modal";
import Button from "../../components/Button";
import PageHeader from "../../components/PageHeader";
import FilterSimple from "../../components/FilterSimple";
import DotStatus from "../../components/DotStatus";
import { useLocation, useNavigate } from "react-router-dom";
import Input from "../../components/Input";
import InputSelect from "../../components/InputSelect";
import api from "../../services/api";
import { renderError } from "../../helpers/errors";
import { toast } from "react-toastify";
import { hasPermission, hasPlanFeature } from "../../services/auth";
import PlanFeatureUpgradeModal from "../../components/PlanFeatureUpgradeModal";
import { ModalDelete } from "../../components/ModalDelete";
import Pagination from "../../components/Pagination";

const types = [
    {label: 'Milheiro', value: 'thousand'},
    {label: 'Unidade/Intervalo', value: 'unit'},
    {label: 'Tamanho', value: 'size'}
];

const multiplyColorsOptions = [
    {label: 'Não', value: false},
    {label: 'Sim', value: true}
];

// Modelos prontos do cálculo por tamanho: criam a gravação já com as medidas típicas
const sizeTemplates = [
    {value: 'textil', label: 'Rolo de 580mm, cobrado por metro', name: null, spacing: 5, sizes: [
        {name: 'Rolo 580mm', width_mm: 580, height_mm: 1000, type: 'roll'}
    ]},
    {value: 'uv', label: 'Folhas A4 e A3', name: null, spacing: 5, sizes: [
        {name: 'Folha A4', width_mm: 210, height_mm: 297, type: 'sheet'},
        {name: 'Folha A3', width_mm: 297, height_mm: 420, type: 'sheet'}
    ]},
    {value: 'custom', label: 'Personalizado — cadastrar as medidas depois', name: null, spacing: null, sizes: []}
];

const customizationSearchParams = [
    {name: 'Nome', key: '[name]', operation: 'contain', type: 'text' }
];

const getCustomizationSearchParam = field => (
    customizationSearchParams.find(param => param.key === field) || customizationSearchParams[0]
);

const normalizePage = page => {
    const parsedPage = parseInt(page, 10);
    return Number.isNaN(parsedPage) || parsedPage < 1 ? 1 : parsedPage;
};

const parseCustomizationListFilters = search => {
    const params = new URLSearchParams(search);
    const searchParam = getCustomizationSearchParam(params.get('search_field'));

    return {
        page: normalizePage(params.get('page')),
        searchField: searchParam.key,
        searchOperation: params.get('search_operation') || searchParam.operation || 'contain',
        search: params.get('search') || '',
    };
};

const buildCustomizationApiQuery = filters => {
    const params = new URLSearchParams();

    if (filters.search.trim() !== '') {
        params.append(`filters[0]${filters.searchField}[${filters.searchOperation}]`, filters.search.trim());
    }

    params.set('page', filters.page);

    return params.toString();
};

const buildCustomizationListSearch = filters => {
    const params = new URLSearchParams();

    if (filters.page > 1) params.set('page', filters.page);
    if (filters.search.trim() !== '') {
        params.set('search_field', filters.searchField);
        params.set('search_operation', filters.searchOperation);
        params.set('search', filters.search.trim());
    }

    return params.toString();
};

export const CustomizationList = () => {
    const [loading, setLoading] = useState(false);

    const [customizations, setCustomizations] = useState([]);
    const [data, setData] = useState({header: [], rows: []});

    const [showCreateCustomization, setShowCreateCustomization] = useState(false);

    const [customizationName, setCustomizationName] =  useState('');
    const [customizationCalculationPer, setCustomizationCalculationPer] = useState(null);
    const [customizationMultiplyColors, setCustomizationMultiplyColors] = useState(null);
    const [customizationSizeTemplate, setCustomizationSizeTemplate] = useState(null);
    const [customizationDefaultSpacingMm, setCustomizationDefaultSpacingMm] = useState('');

    const [customizationToDelete, setCustomizationToDelete] = useState(null);

    const [currentPage, setCurrentPage] = useState(1);
    const [lastPage, setLastPage] = useState(1);
    const [perPage, setPerPage] = useState(20);
    const [totalResults, setTotalResults] = useState(0);
    const [hasNextPage, setHasNextPage] = useState(false);
    const [hasPrevPage, setHasPrevPage] = useState(false);

    const [selectedIds, setSelectedIds] = useState([]);
    const [showConfirmMultipleDelete, setShowConfirmMultipleDelete] = useState(false);

    const navigate = useNavigate();
    const location = useLocation();
    const customizationFilters = useMemo(() => parseCustomizationListFilters(location.search), [location.search]);

    const updateCustomizationListUrl = (changes, resetPage = true) => {
        const nextFilters = {
            ...customizationFilters,
            ...changes,
            page: resetPage ? 1 : (changes.page || customizationFilters.page),
        };
        const search = buildCustomizationListSearch(nextFilters);

        navigate({
            pathname: location.pathname,
            search: search ? `?${search}` : '',
        });
    }

    useEffect(() => {
        getCustomizations();
    }, [location.search]);

    useEffect(() => {
        generateData();
    }, [customizations, location.search]);

    useEffect(() => {
        if(!showCreateCustomization) clean();
    }, [showCreateCustomization]);

    const getCustomizations = () => {
        setCustomizations([]);
        setLoading(true);

        api.get(`/customization/paginate?${buildCustomizationApiQuery(customizationFilters)}`).then(res => {
            setCustomizations(res.data.resources.data);
            setCurrentPage(res.data.resources.current_page);
            setLastPage(res.data.resources.last_page)
            setPerPage(res.data.resources.per_page)
            setTotalResults(res.data.resources.total)
            setHasNextPage(res.data.resources.next_page_url !== null);
            setHasPrevPage(res.data.resources.prev_page_url !== null);
        }).catch(error => {
            renderError(error);
        }).then(() => setLoading(false));
    }

    const generateData = () => {
        let header = ['Nome', 'Cálculo por', 'Última atualização', 'Cadastro'];

        if(hasPermission('delete_customization')){
            header.push('');
        }
        
        const rows = [];

        customizations.map(customization => {
            let buttons = [];
            const returnTo = encodeURIComponent(`/customization/list${location.search}`);

            if(hasPermission('delete_customization')){
                buttons.push(
                    <Button type={`delete`} svg={`delete`} action={() => setCustomizationToDelete(customization.id)} />
                );
            }
            rows.push({
                data: [
                    customization.name,
                    types.find(type => type.value === customization.calculation_per)?.label ?? customization.calculation_per,
                    moment(customization.updated_at).format('DD/MM/YYYY HH:mm'),
                    moment(customization.created_at).format('DD/MM/YYYY HH:mm'),
                ],
                to: `/customization/edit/${customization.id}?return_to=${returnTo}`,
                buttons,
                id: customization.id
            })
        });

        setData({header, rows});
    }

    const handleSizeTemplateChange = value => {
        setCustomizationSizeTemplate(value);

        // pré-preenche o nome, sem sobrescrever um nome digitado pelo usuário
        const template = sizeTemplates.find(t => t.value === value);
        const templateNames = sizeTemplates.map(t => t.name);
        if(template?.name && (customizationName === '' || templateNames.includes(customizationName))){
            setCustomizationName(template.name);
        }

        setCustomizationDefaultSpacingMm(template?.spacing ?? '');
    }

    // aceita vazio (sem espaçamento definido), diferente da máscara convertToInt que vira 0
    const handleDefaultSpacingChange = value => {
        const digits = value.toString().replace(/\D/g, '');
        setCustomizationDefaultSpacingMm(digits === '' ? '' : parseInt(digits, 10));
    }

    const storeCustomization = () => {
        setLoading(true);

        const isSize = customizationCalculationPer === 'size';
        const template = isSize ? sizeTemplates.find(t => t.value === customizationSizeTemplate) : null;

        api.post(`/customization`, {
            name: customizationName,
            calculation_per: customizationCalculationPer,
            multiply_colors: isSize ? false : customizationMultiplyColors,
            default_spacing_mm: isSize && customizationDefaultSpacingMm !== '' ? customizationDefaultSpacingMm : null
        }).then(async res => {
            const customizationId = res.data.resource.id;

            // modelos prontos já nascem com as medidas típicas cadastradas
            for(const size of (template?.sizes ?? [])){
                await api.post(`/customization/${customizationId}/size`, size);
            }

            toast.success('Gravação cadastrada com sucesso');
            navigate(`/customization/edit/${customizationId}`);
        }).catch(error => {
            renderError(error);
            setLoading(false);
        });
    }

    const clean = () => {
        setCustomizationName('');
        setCustomizationCalculationPer(null);
        setCustomizationSizeTemplate(null);
        setCustomizationDefaultSpacingMm('');
    }

    const deleteCustomization = () => {
        setLoading(true);

        api.delete(`/customization/${customizationToDelete}`).then(res => {
            setCustomizationToDelete(null);
            getCustomizations();
        }).catch(error => {
            renderError(error);
            setLoading(false);
        });
    }

    const deleteMultipleCustomizations = () => {
        setLoading(true);

        api.delete(`/customization/multiple`, {
            data: {
                ids: selectedIds
            }
        }).then(res => {
            setSelectedIds([]);
            getCustomizations();
            setShowConfirmMultipleDelete(false);
        }).catch(error => {
            renderError(error);
            setLoading(false);
        });
    }

    const handleSetFilter = (value, metadata) => {
        updateCustomizationListUrl({
            searchField: metadata?.field || customizationFilters.searchField,
            searchOperation: metadata?.operation || customizationFilters.searchOperation,
            search: metadata?.value ?? '',
        });
    }

    const handleSetPage = page => {
        updateCustomizationListUrl({ page }, false);
    }

    if (!hasPlanFeature('calculation')) {
        return (
            <PlanFeatureUpgradeModal
                feature="calculation"
                show={true}
                close={() => navigate(-1)}
            />
        );
    }

    return (
        <>
            <ModalDelete
                show={showConfirmMultipleDelete}
                text={<p>Tem certeza que deseja apagar <strong>{selectedIds.length} {selectedIds.length > 1 ? 'gravações' : 'gravação'}</strong>?</p>}
                action={deleteMultipleCustomizations}
                loading={loading}
                close={() => setShowConfirmMultipleDelete(false)}
            />
            <ModalDelete
                show={customizationToDelete !== null}
                action={deleteCustomization}
                loading={loading}
                close={() => setCustomizationToDelete(null)}
            />
            <Modal
                title={`Cadastrar gravação`}
                size={`big`}
                show={showCreateCustomization}
                close={() => setShowCreateCustomization(false)}
                footer={
                    <Button
                        type={`primary`}
                        size={`small`}
                        text={`Cadastrar`}
                        svg={`save-white`}
                        action={storeCustomization}
                        loading={loading} 
                    />
                }
            >
                <div className="section">
                    <div className="row">
                        <div className="col-4">
                            <Input type={`text`} label={`Nome *`} value={customizationName} change={setCustomizationName} />
                        </div>
                        <div className="col-4">
                            <InputSelect label={`Cálculo por *`} value={customizationCalculationPer} change={setCustomizationCalculationPer} options={types} />
                        </div>
                        {customizationCalculationPer !== 'size' &&
                            <div className="col-4">
                                <InputSelect label={`Multiplicar pelas cores *`} value={customizationMultiplyColors} change={setCustomizationMultiplyColors} options={multiplyColorsOptions} />
                            </div>
                        }
                    </div>

                    {customizationCalculationPer === 'size' &&
                        <div className="size-setup">
                            <div className="size-setup-info">
                                <strong>Como funciona:</strong> o custo é calculado por quantas folhas (ou metros de rolo) as artes ocupam.
                                Escolha um modelo para criar as medidas típicas automaticamente — depois é só cadastrar os custos por fornecedor.
                            </div>
                            <div className="row">
                                <div className="col-6">
                                    <InputSelect
                                        label={`Modelo *`}
                                        value={customizationSizeTemplate}
                                        change={handleSizeTemplateChange}
                                        options={sizeTemplates.map(template => ({label: template.label, value: template.value}))}
                                    />
                                </div>
                                <div className="col-6">
                                    <Input
                                        label={`Espaçamento padrão (mm)`}
                                        value={customizationDefaultSpacingMm}
                                        change={handleDefaultSpacingChange}
                                    />
                                    <p className="input-hint">Folga entre uma arte e outra na impressão — ajustável depois em cada orçamento.</p>
                                </div>
                            </div>
                        </div>
                    }
                </div>
            </Modal>
            <PanelTemplate id="customization-list">
                <PageHeader
                    left={
                        <FilterSimple
                            params={customizationSearchParams}
                            filter={handleSetFilter}
                            externalValue={customizationFilters.search}
                            externalOption={customizationFilters.searchField}
                        />
                    }

                    right={
                        <>
                            {hasPermission('delete_customization') && selectedIds.length > 0 &&
                                <Button 
                                type={`secondary`} 
                                size={`small`} 
                                svg={`delete`}
                                text={`Excluir ${selectedIds.length} selecionados`} 
                                action={() => setShowConfirmMultipleDelete(true)}  
                                />
                            }
                            {hasPermission('create_customization') &&
                                <Button type={`primary`} size={`small`} text={`Cadastrar gravação`} svg="plus-white" action={() => setShowCreateCustomization(true)}  />
                            }
                        </>
                    }
                />
                
                <Box className={`no-padding with-pagination`}>
                    <Table 
                        header={data.header} 
                        rows={data.rows} 
                        loading={loading}
                        checks={true} 
                        selectedIds={selectedIds}
                        setSelectedIds={setSelectedIds}
                    />
                </Box>
                {!loading &&
                    <Pagination
                        currentPage={currentPage}
                        lastPage={lastPage}
                        perPage={perPage}
                        totalResults={totalResults}
                        hasNextPage={hasNextPage}
                        hasPrevPage={hasPrevPage}
                        setCurrentPage={handleSetPage}
                    />
                }
            </PanelTemplate>
        </>
    );
}

export default CustomizationList;